import { v4 as uuidv4 } from "uuid";
import { notify } from "../api/photoshop";
import { photoshop } from "../globals";
import { useListenFromRenderer } from "../message";
import { postToRenderer } from "../message/utils";
import { createBase64FromRawPixels } from "../utils/jimp";
import { isCropToolActive } from "./shared/check-crop-tool";

const TOOL_NAME = "Film Grain";
const MAX_DIM = 4096;

class NoisePatchGenerator {
	size: number;
	r: number;
	randMove: number;
	batch: Float32Array[];

	constructor(size: number) {
		this.size = size;
		this.r = (size * 8.4) / 40;
		this.randMove = Math.floor(size / 2) + this.r;
		this.batch = [];
	}

	makeBatch(n: number) {
		const K = this.size;
		const r2 = this.r * this.r;
		const rm = this.randMove;
		const half = (K - 1) / 2;
		this.batch = new Array(n);
		for (let b = 0; b < n; b++) {
			const tx = half + (0.5 - Math.random()) * 2 * rm;
			const ty = half + (0.5 - Math.random()) * 2 * rm;
			const patch = new Float32Array(K * K);
			for (let i = 0; i < K; i++) {
				for (let j = 0; j < K; j++) {
					const dx = i - tx;
					const dy = j - ty;
					if (dx * dx + dy * dy < r2) patch[i * K + j] = 1.0;
				}
			}
			this.batch[b] = patch;
		}
	}
}

async function processGrainChannel(
	data: Float32Array,
	H: number,
	W: number,
	ksize: number,
	fac: number,
	onProgress?: (rowsDone: number, totalRows: number) => void,
): Promise<Float32Array> {
	let maxVal = 0;
	for (let i = 0; i < data.length; i++) if (data[i] > maxVal) maxVal = data[i];
	const scale = maxVal > 2 ? 1 / 255 : 1;

	const input = new Float32Array(H * W);
	for (let i = 0; i < H * W; i++) input[i] = Math.min(data[i] * scale, 1);

	const patchGen = new NoisePatchGenerator(ksize);
	patchGen.makeBatch(5000);

	const batch = patchGen.batch;
	const N = batch.length;
	const K2 = ksize * ksize;

	const nAll = new Int32Array(H * W);
	for (let i = 0; i < H * W; i++) {
		const safe = Math.min(input[i], 0.9999);
		const n = ((-fac * Math.log(1.001 - safe)) + (Math.random() * 4 - 2)) | 0;
		nAll[i] = n > 0 ? n : 0;
	}

	const noise = new Float32Array(H * W);
	const acc = new Float32Array(W * K2);
	const YIELD_EVERY = 32;

	for (let i = 0; i < H; i++) {
		const rowBase = i * W;
		let cap = 0;
		for (let j = 0; j < W; j++) if (nAll[rowBase + j] > cap) cap = nAll[rowBase + j];

		if (cap > 0) {
			acc.fill(0);
			for (let step = 0; step < cap; step++) {
				for (let j = 0; j < W; j++) {
					if (nAll[rowBase + j] <= step) continue;
					const patch = batch[(Math.random() * N) | 0];
					const base = j * K2;
					for (let k = 0; k < K2; k++) acc[base + k] += patch[k];
				}
			}
			for (let j = 0; j < W; j++) {
				const base = j * K2;
				let sum = 0;
				for (let k = 0; k < K2; k++) {
					const v = acc[base + k];
					sum += v > 1 ? 1 : v;
				}
				noise[rowBase + j] = sum / K2;
			}
		}

		if (onProgress && i % YIELD_EVERY === 0) {
			onProgress(i, H);
			await new Promise<void>((r) => setTimeout(r, 0));
		}
	}

	onProgress?.(H, H);
	return noise;
}

function extractChannels(
	raw: Uint8Array,
	pixelCount: number,
	channels: number,
): { r: Float32Array; g: Float32Array; b: Float32Array } {
	const r = new Float32Array(pixelCount);
	const g = new Float32Array(pixelCount);
	const b = new Float32Array(pixelCount);
	for (let i = 0; i < pixelCount; i++) {
		r[i] = raw[i * channels];
		g[i] = raw[i * channels + 1];
		b[i] = raw[i * channels + 2];
	}
	return { r, g, b };
}

async function buildGrainRGBA(
	raw: Uint8Array,
	channels: number,
	H: number,
	W: number,
	mode: "mono" | "bw" | "color",
	ksize: number,
	fac: number,
	onProgress?: (pct: number) => void,
): Promise<Uint8Array> {
	const total = H * W;
	const { r, g, b } = extractChannels(raw, total, channels);
	const out = new Uint8Array(total * 4);

	if (mode === "mono" || mode === "bw") {
		const lum = new Float32Array(total);
		for (let i = 0; i < total; i++) {
			lum[i] = 0.299 * r[i] + 0.587 * g[i] + 0.114 * b[i];
		}
		const noise = await processGrainChannel(lum, H, W, ksize, fac,
			onProgress ? (done, total) => onProgress(done / total) : undefined,
		);
		for (let i = 0; i < total; i++) {
			const v = Math.min(255, (noise[i] * 255 + 0.5) | 0);
			out[i * 4] = v;
			out[i * 4 + 1] = v;
			out[i * 4 + 2] = v;
			out[i * 4 + 3] = 255;
		}
	} else {
		const noiseR = await processGrainChannel(r, H, W, ksize, fac,
			onProgress ? (done, total) => onProgress(done / total / 3) : undefined,
		);
		const noiseG = await processGrainChannel(g, H, W, ksize, fac,
			onProgress ? (done, total) => onProgress(1 / 3 + done / total / 3) : undefined,
		);
		const noiseB = await processGrainChannel(b, H, W, ksize, fac,
			onProgress ? (done, total) => onProgress(2 / 3 + done / total / 3) : undefined,
		);
		for (let i = 0; i < total; i++) {
			out[i * 4] = Math.min(255, (noiseR[i] * 255 + 0.5) | 0);
			out[i * 4 + 1] = Math.min(255, (noiseG[i] * 255 + 0.5) | 0);
			out[i * 4 + 2] = Math.min(255, (noiseB[i] * 255 + 0.5) | 0);
			out[i * 4 + 3] = 255;
		}
	}

	return out;
}

function resampleAreaAverage(
	src: Uint8Array,
	srcW: number,
	srcH: number,
	dstW: number,
	dstH: number,
): Uint8Array {
	const dst = new Uint8Array(dstW * dstH * 4);
	const sx = srcW / dstW;
	const sy = srcH / dstH;
	for (let y = 0; y < dstH; y++) {
		const y0 = y * sy;
		const y1 = y0 + sy;
		for (let x = 0; x < dstW; x++) {
			const x0 = x * sx;
			const x1 = x0 + sx;
			let r = 0, g = 0, b = 0, a = 0, totalW = 0;
			for (let iy = Math.floor(y0); iy < Math.ceil(y1) && iy < srcH; iy++) {
				const wy = Math.min(iy + 1, y1) - Math.max(iy, y0);
				for (let ix = Math.floor(x0); ix < Math.ceil(x1) && ix < srcW; ix++) {
					const w = wy * (Math.min(ix + 1, x1) - Math.max(ix, x0));
					const i = (iy * srcW + ix) * 4;
					r += src[i] * w;
					g += src[i + 1] * w;
					b += src[i + 2] * w;
					a += src[i + 3] * w;
					totalW += w;
				}
			}
			const d = (y * dstW + x) * 4;
			dst[d]     = Math.round(r / totalW);
			dst[d + 1] = Math.round(g / totalW);
			dst[d + 2] = Math.round(b / totalW);
			dst[d + 3] = Math.round(a / totalW);
		}
	}
	return dst;
}

function resampleNN(
	src: Uint8Array,
	srcW: number,
	srcH: number,
	dstW: number,
	dstH: number,
): Uint8Array {
	const dst = new Uint8Array(dstW * dstH * 4);
	for (let y = 0; y < dstH; y++) {
		const sy = Math.min(((y * srcH) / dstH) | 0, srcH - 1);
		for (let x = 0; x < dstW; x++) {
			const sx = Math.min(((x * srcW) / dstW) | 0, srcW - 1);
			const s = (sy * srcW + sx) * 4;
			const d = (y * dstW + x) * 4;
			dst[d] = src[s];
			dst[d + 1] = src[s + 1];
			dst[d + 2] = src[s + 2];
			dst[d + 3] = src[s + 3];
		}
	}
	return dst;
}

function applyBrightnessCorrection(blended: Uint8Array, orig: Uint8Array, total: number): Uint8Array {
	let origSum = 0, blendSum = 0, visible = 0;
	for (let i = 0; i < total; i++) {
		const pi = i * 4;
		// 완전 투명 픽셀은 보정 계산에서 제외 (RGB가 무의미한 값)
		if (orig[pi + 3] === 0) continue;
		visible++;
		origSum  += orig[pi] + orig[pi + 1] + orig[pi + 2];
		blendSum += blended[pi] + blended[pi + 1] + blended[pi + 2];
	}
	if (visible === 0) return blended;
	const correction = Math.round((origSum - blendSum) / (visible * 3));
	if (correction === 0) return blended;
	const out = new Uint8Array(blended);
	for (let i = 0; i < total; i++) {
		const pi = i * 4;
		out[pi]     = Math.max(0, Math.min(255, blended[pi]     + correction));
		out[pi + 1] = Math.max(0, Math.min(255, blended[pi + 1] + correction));
		out[pi + 2] = Math.max(0, Math.min(255, blended[pi + 2] + correction));
	}
	return out;
}

// mono: luminance deviation — orig[c] + (grain_lum - orig_lum) * t
// bw/color: lerp(orig, grain, t)
function blendGrainWithOrig(
	orig: Uint8Array,
	grain: Uint8Array,
	total: number,
	mode: "mono" | "bw" | "color",
	intensity: number,
): Uint8Array {
	const out = new Uint8Array(total * 4);
	const t = Math.max(0, Math.min(1, intensity));

	if (mode === "mono") {
		for (let i = 0; i < total; i++) {
			const pi = i * 4;
			const ro = orig[pi];
			const go = orig[pi + 1];
			const bo = orig[pi + 2];
			const lum = 0.299 * ro + 0.587 * go + 0.114 * bo;
			const grainVal = (grain[pi] - lum) * t;
			out[pi]     = Math.max(0, Math.min(255, Math.round(ro + grainVal)));
			out[pi + 1] = Math.max(0, Math.min(255, Math.round(go + grainVal)));
			out[pi + 2] = Math.max(0, Math.min(255, Math.round(bo + grainVal)));
			// 원본의 투명도 유지 (투명 영역이 불투명해지지 않도록)
			out[pi + 3] = orig[pi + 3];
		}
	} else {
		for (let i = 0; i < total; i++) {
			const pi = i * 4;
			out[pi]     = Math.round(orig[pi]     * (1 - t) + grain[pi]     * t);
			out[pi + 1] = Math.round(orig[pi + 1] * (1 - t) + grain[pi + 1] * t);
			out[pi + 2] = Math.round(orig[pi + 2] * (1 - t) + grain[pi + 2] * t);
			out[pi + 3] = orig[pi + 3];
		}
	}

	return out;
}

async function getDocumentPixelsRGBA(maxDim: number): Promise<{
	rgba: Uint8Array;
	srcW: number;
	srcH: number;
	docW: number;
	docH: number;
} | null> {
	const doc = photoshop.app.activeDocument;
	if (!doc) {
		await notify("Open a PSD file first");
		return null;
	}
	if (doc.mode !== "RGBColorMode" || doc.bitsPerChannel !== "bitDepth8") {
		await notify("Image mode must be RGB Color, 8bits/Channel");
		return null;
	}

	const docW = doc.width;
	const docH = doc.height;

	const { imageData, sourceBounds } = await photoshop.imaging.getPixels({
		documentID: doc.id,
		sourceBounds: { left: 0, top: 0, right: docW, bottom: docH },
		colorProfile: "sRGB IEC61966-2.1",
	});

	const rawData = (await imageData.getData({})) as Uint8Array;
	imageData.dispose();

	const srcW = sourceBounds.right - sourceBounds.left;
	const srcH = sourceBounds.bottom - sourceBounds.top;
	const pixelCount = srcW * srcH;
	const channels = rawData.length / pixelCount;

	if (channels !== 3 && channels !== 4) {
		throw new Error(`Unexpected channel count: ${channels}`);
	}

	let rgba: Uint8Array;
	if (channels === 4) {
		rgba = rawData;
	} else {
		rgba = new Uint8Array(pixelCount * 4);
		for (let i = 0; i < pixelCount; i++) {
			rgba[i * 4] = rawData[i * 3];
			rgba[i * 4 + 1] = rawData[i * 3 + 1];
			rgba[i * 4 + 2] = rawData[i * 3 + 2];
			rgba[i * 4 + 3] = 255;
		}
	}

	const scale = Math.min(maxDim / Math.max(srcW, srcH), 1);
	if (scale < 1) {
		const w = Math.max(1, Math.round(srcW * scale));
		const h = Math.max(1, Math.round(srcH * scale));
		return { rgba: resampleNN(rgba, srcW, srcH, w, h), srcW: w, srcH: h, docW, docH };
	}

	return { rgba, srcW, srcH, docW, docH };
}

type PendingGrain = {
	origRGBA: Uint8Array;
	grainRGBA: Uint8Array;
	srcW: number;
	srcH: number;
	docW: number;
	docH: number;
	mode: "mono" | "bw" | "color";
};

const pendingGrains = new Map<string, PendingGrain>();
let grainAbortFlag = false;

async function buildAndSendPreview(
	blended: Uint8Array,
	srcW: number,
	srcH: number,
	previewId: string,
	origPixels?: Uint8Array,
): Promise<void> {
	const previewScale = Math.min(MAX_DIM / Math.max(srcW, srcH), 1);
	const prevW = Math.max(1, Math.round(srcW * previewScale));
	const prevH = Math.max(1, Math.round(srcH * previewScale));

	const previewPixels = previewScale < 1
		? resampleAreaAverage(blended, srcW, srcH, prevW, prevH)
		: blended;
	const previewBase64 = await createBase64FromRawPixels(previewPixels, prevW, prevH);

	let origBase64: string | undefined;
	if (origPixels) {
		const origScaled = previewScale < 1
			? resampleAreaAverage(origPixels, srcW, srcH, prevW, prevH)
			: origPixels;
		origBase64 = await createBase64FromRawPixels(origScaled, prevW, prevH);
	}

	postToRenderer([{
		key: "filmGrainPreviewResult",
		id: uuidv4(),
		values: { previewId, previewBase64, origBase64, width: prevW, height: prevH },
	}]);
}

async function handleFilmGrainPreview(
	_messageId: string,
	params: { mode: "mono" | "bw" | "color"; size: number; density: number; intensity: number; brightnessCorrect?: boolean },
): Promise<boolean> {
	let success = false;

	if (isCropToolActive()) {
		await notify("Cannot apply Film Grain while the Crop tool is active.");
		return false;
	}

	await photoshop.core.executeAsModal(
		async (executionContext) => {
			executionContext.onCancel = () => { grainAbortFlag = true; };
			const report = (value: number) => executionContext.reportProgress({ value });
			try {
				report(0);

				const pixels = await getDocumentPixelsRGBA(MAX_DIM);
				if (!pixels) return;

				report(0.05);

				const { rgba, srcW, srcH, docW, docH } = pixels;

				const grainRGBA = await buildGrainRGBA(
					rgba, 4, srcH, srcW, params.mode, params.size, params.density,
					(pct) => {
						if (grainAbortFlag) throw new Error("__grain_cancelled__");
						report(0.05 + pct * 0.85);
					},
				);

				report(0.9);

				const previewId = uuidv4();
				pendingGrains.set(previewId, { origRGBA: rgba, grainRGBA, srcW, srcH, docW, docH, mode: params.mode });

				let blended = blendGrainWithOrig(rgba, grainRGBA, srcW * srcH, params.mode, params.intensity);
				if (params.brightnessCorrect) blended = applyBrightnessCorrection(blended, rgba, srcW * srcH);

				report(0.95);

				await buildAndSendPreview(blended, srcW, srcH, previewId, rgba);

				success = true;
			} catch (err) {
				if (err instanceof Error && err.message === "__grain_cancelled__") return;
				console.error(err);
				await notify(`Film Grain Preview error: ${err}`);
			}
		},
		{ commandName: TOOL_NAME },
	);

	return success;
}

async function handleFilmGrainUpdatePreview(params: {
	previewId: string;
	intensity: number;
	brightnessCorrect?: boolean;
}): Promise<void> {
	const stored = pendingGrains.get(params.previewId);
	if (!stored) return;

	try {
		const { origRGBA, grainRGBA, srcW, srcH, mode } = stored;
		let blended = blendGrainWithOrig(origRGBA, grainRGBA, srcW * srcH, mode, params.intensity);
		if (params.brightnessCorrect) blended = applyBrightnessCorrection(blended, origRGBA, srcW * srcH);
		await buildAndSendPreview(blended, srcW, srcH, params.previewId);
	} catch (err) {
		console.error(err);
	}
}

async function handleFilmGrainApply(params: { previewId: string; intensity: number; brightnessCorrect?: boolean }): Promise<boolean> {
	const stored = pendingGrains.get(params.previewId);
	if (!stored) {
		await notify("Preview expired. Please generate a new preview.");
		return false;
	}

	if (isCropToolActive()) {
		await notify("Cannot apply Film Grain while the Crop tool is active.");
		return false;
	}

	let success = false;

	await photoshop.core.executeAsModal(
		async () => {
			const doc = photoshop.app.activeDocument;
			if (!doc) {
				await notify("Open a PSD file first");
				return;
			}

			await doc.suspendHistory(async () => {
				try {
					const { origRGBA, grainRGBA, srcW, srcH, docW, docH, mode } = stored;

					let blended = blendGrainWithOrig(origRGBA, grainRGBA, srcW * srcH, mode, params.intensity);
					if (params.brightnessCorrect) blended = applyBrightnessCorrection(blended, origRGBA, srcW * srcH);

					const blendedFull =
						srcW !== docW || srcH !== docH
							? resampleNN(blended, srcW, srcH, docW, docH)
							: blended;

					const imageData = await photoshop.imaging.createImageDataFromBuffer(
						blendedFull,
						{ width: docW, height: docH, components: 4, colorSpace: "RGB", colorProfile: "sRGB IEC61966-2.1" },
					);

					const newLayer = await doc.createPixelLayer({ name: TOOL_NAME });
					if (!newLayer) {
						imageData.dispose();
						throw new Error("Failed to create layer");
					}

					await photoshop.imaging.putPixels({
						documentID: doc.id,
						layerID: newLayer.id,
						imageData,
						targetBounds: { left: 0, top: 0, right: docW, bottom: docH },
						replace: true,
					});

					imageData.dispose();
					success = true;
				} catch (err) {
					console.error(err);
					await notify(`Film Grain error: ${err}`);
				}
			}, TOOL_NAME);
		},
		{ commandName: TOOL_NAME },
	);

	return success;
}

export function useListenFilmGrainMessage() {
	useListenFromRenderer([
		{
			key: "filmGrainPreview",
			onMessage: async (message) => {
				grainAbortFlag = false;
				let success = false;
				try {
					success = await handleFilmGrainPreview(message.id, message.values);
				} catch (err) {
					console.error(err);
				} finally {
					postToRenderer([{
						key: "finish",
						id: uuidv4(),
						values: { id: message.id, status: success ? "success" : "error" },
					}]);
				}
			},
		},
		{
			key: "filmGrainApply",
			onMessage: async (message) => {
				const success = await handleFilmGrainApply(message.values);
				postToRenderer([{
					key: "finish",
					id: uuidv4(),
					values: { id: message.id, status: success ? "success" : "error" },
				}]);
			},
		},
		{
			key: "filmGrainUpdatePreview",
			onMessage: async (message) => {
				await handleFilmGrainUpdatePreview(message.values);
				postToRenderer([{
					key: "finish",
					id: uuidv4(),
					values: { id: message.id, status: "success" },
				}]);
			},
		},
		{
			key: "filmGrainPreviewClose",
			onMessage: (message) => {
				pendingGrains.delete(message.values.previewId);
			},
		},
	]);
}
